class PriceRangeCurrencyBridge extends HTMLElement {
    constructor() {
      super();
  
      this._holder = null;
      this._rangeEl = null;
  
      this._minDisplay = null;
      this._maxDisplay = null;
      this._minHidden = null;
      this._maxHidden = null;
  
      this._currencySpans = null;
  
      this._debouncedDisplayToAed = null;
  
      this._onStorage = null;
      this._onCurrencyChanged = null;
  
      this._isSyncing = false;
  
      this._rangeObserver = null;
      this._activeFiltersObserver = null;
    }
  
    connectedCallback() {
      this._holder = this.closest('[data-range-holder]');
      if (!this._holder) return;
  
      this._rangeEl = this._holder.querySelector('[data-range-slider]');
      this._minDisplay = this._holder.querySelector('[data-field-price-min-display]');
      this._maxDisplay = this._holder.querySelector('[data-field-price-max-display]');
      this._minHidden = this._holder.querySelector('[data-field-price-min]');
      this._maxHidden = this._holder.querySelector('[data-field-price-max]');
      this._currencySpans = this._holder.querySelectorAll('[data-filter-currency-symbol]');
  
      if (!this._rangeEl || !this._minDisplay || !this._maxDisplay || !this._minHidden || !this._maxHidden) return;
  
      let debounceMs = this._toNumber(this.getAttribute('debounce'));
      if (debounceMs === null) debounceMs = 350;
  
      this._debouncedDisplayToAed = this._debounce(this._syncDisplayToAed.bind(this), debounceMs);
  
      this._minDisplay.addEventListener('input', this._debouncedDisplayToAed);
      this._maxDisplay.addEventListener('input', this._debouncedDisplayToAed);
      this._minDisplay.addEventListener('change', this._syncDisplayToAed.bind(this));
      this._maxDisplay.addEventListener('change', this._syncDisplayToAed.bind(this));
      this._minDisplay.addEventListener('blur', this._syncDisplayToAed.bind(this));
      this._maxDisplay.addEventListener('blur', this._syncDisplayToAed.bind(this));
  
      this._minHidden.addEventListener('input', this._syncAedToDisplay.bind(this));
      this._maxHidden.addEventListener('input', this._syncAedToDisplay.bind(this));
      this._minHidden.addEventListener('change', this._syncAedToDisplay.bind(this));
      this._maxHidden.addEventListener('change', this._syncAedToDisplay.bind(this));
  
      this._rangeEl.addEventListener('input', this._syncAedToDisplay.bind(this));
      this._rangeEl.addEventListener('change', this._syncAedToDisplay.bind(this));
      this._rangeEl.addEventListener('range:refresh', this._syncAedToDisplay.bind(this));
  
      this._rangeObserver = new MutationObserver(this._syncAedToDisplay.bind(this));
      this._rangeObserver.observe(this._rangeEl, { attributes: true, attributeFilter: ['data-se-min-value', 'data-se-max-value'] });
  
      this._onStorage = this._handleStorage.bind(this);
      window.addEventListener('storage', this._onStorage);
  
      this._onCurrencyChanged = this._handleCurrencyChanged.bind(this);
      document.addEventListener('currency:changed', this._onCurrencyChanged);
  
      this._syncAedToDisplay();
      this._updateActivePriceFilterLabel();
  
      window.setTimeout(this._updateActivePriceFilterLabel.bind(this), 0);
      window.setTimeout(this._syncAedToDisplay.bind(this), 200);
    }
  
    disconnectedCallback() {
      if (this._rangeObserver) this._rangeObserver.disconnect();
      if (this._activeFiltersObserver) this._activeFiltersObserver.disconnect();
      if (this._onStorage) window.removeEventListener('storage', this._onStorage);
      if (this._onCurrencyChanged) document.removeEventListener('currency:changed', this._onCurrencyChanged);
    }
  
    _handleStorage(e) {
      if (!e || e.key !== 'currencyExchangeData') return;
      this._syncAedToDisplay();
      this._updateActivePriceFilterLabel();
    }
  
    _handleCurrencyChanged() {
      this._syncAedToDisplay();
      this._updateActivePriceFilterLabel();
    }
  
    _syncDisplayToAed() {
      if (this._isSyncing) return;
      this._isSyncing = true;
  
      const currencyData = this._getCurrencyData();
      const selected = currencyData.selectedCurrency || 'AED';
      const rate = this._getRate(currencyData);
  
      this._setCurrencySymbol(selected);
  
      if (selected !== 'AED' && rate === null) {
        this._isSyncing = false;
        return;
      }
  
      const minDisplayVal = this._toNumber(this._minDisplay.value);
      const maxDisplayVal = this._toNumber(this._maxDisplay.value);
  
      if (minDisplayVal === null) this._minHidden.value = '';
      if (maxDisplayVal === null) this._maxHidden.value = '';
  
      let minAed = null;
      let maxAed = null;
  
      if (minDisplayVal !== null) {
        minAed = selected === 'AED' ? Math.floor(minDisplayVal) : Math.floor(minDisplayVal / rate);
        this._minHidden.value = String(minAed);
      }
  
      if (maxDisplayVal !== null) {
        maxAed = selected === 'AED' ? Math.ceil(maxDisplayVal) : Math.ceil(maxDisplayVal / rate);
        this._maxHidden.value = String(maxAed);
      }
  
      let currentMin = this._toNumber(this._rangeEl.getAttribute('data-se-min-value'));
      let currentMax = this._toNumber(this._rangeEl.getAttribute('data-se-max-value'));
      if (currentMin === null) currentMin = 0;
      if (currentMax === null) currentMax = 0;
  
      const nextMin = minAed === null ? currentMin : minAed;
      const nextMax = maxAed === null ? currentMax : maxAed;
  
      this._refreshRangeSlider(this._rangeEl, nextMin, nextMax);
      this._updateActivePriceFilterLabel();
  
      this._isSyncing = false;
    }
  
    _syncAedToDisplay() {
      if (this._isSyncing) return;
      this._isSyncing = true;
  
      const currencyData = this._getCurrencyData();
      const selected = currencyData.selectedCurrency || 'AED';
      const rate = this._getRate(currencyData);
  
      this._setCurrencySymbol(selected);
  
      if (selected !== 'AED' && rate === null) {
        this._isSyncing = false;
        return;
      }
  
      let minAed = this._toNumber(this._minHidden.value);
      let maxAed = this._toNumber(this._maxHidden.value);
  
      if (minAed === null) {
        minAed = this._toNumber(this._rangeEl.getAttribute('data-se-min-value'));
      }
      if (maxAed === null) {
        maxAed = this._toNumber(this._rangeEl.getAttribute('data-se-max-value'));
      }
  
      if (minAed !== null) {
        const minDisplay = selected === 'AED' ? minAed : Math.round(minAed * rate);
        this._minDisplay.value = String(minDisplay);
        this._minDisplay.placeholder = String(minDisplay);
      }
  
      if (maxAed !== null) {
        const maxDisplay = selected === 'AED' ? maxAed : Math.round(maxAed * rate);
        this._maxDisplay.value = String(maxDisplay);
        this._maxDisplay.placeholder = String(maxDisplay);
      }
  
      this._updateActivePriceFilterLabel();
  
      this._isSyncing = false;
    }
  
    _updateActivePriceFilterLabel() {
      const link = document.querySelector('[data-active-price-filter]');
      if (!link) return;
  
      const moneySpans = link.querySelectorAll('span.money');
      if (!moneySpans || moneySpans.length < 2) return;
  
      const params = new URLSearchParams(window.location.search);
  
      const gteRaw = params.get('filter.v.price.gte');
      const lteRaw = params.get('filter.v.price.lte');
  
      if (gteRaw === null && lteRaw === null) return;
  
      let minAed = gteRaw === null ? 0 : Number(gteRaw);
      const maxAed = lteRaw === null ? null : Number(lteRaw);
  
      if (!Number.isFinite(minAed)) minAed = 0;
  
      const currencyData = this._getCurrencyData();
      const selected = currencyData.selectedCurrency || 'AED';
      const rate = this._getRate(currencyData);
  
      if (selected !== 'AED' && rate === null) return;
  
      const minDisplay = selected === 'AED' ? minAed : Math.round(minAed * rate);
  
      let maxDisplay = null;
      if (maxAed !== null && Number.isFinite(maxAed)) {
        maxDisplay = selected === 'AED' ? maxAed : Math.round(maxAed * rate);
      }
  
      moneySpans[0].textContent = this._formatCurrency(minDisplay, selected);
  
      if (maxDisplay === null) return;
  
      moneySpans[1].textContent = this._formatCurrency(maxDisplay, selected);
    }
  
    _parseMoneyNumber(text) {
      if (!text) return null;
      let cleaned = String(text).replace(/[^\d.,-]/g, '');
      cleaned = cleaned.replace(/,/g, '');
      const n = Number(cleaned);
      if (!Number.isFinite(n)) return null;
      return n;
    }
  
    _formatCurrency(value, currencyCode) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 0
        }).format(value);
      } catch (e) {
        return String(value) + ' ' + currencyCode;
      }
    }
  
    _safeParseJson(value) {
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    }
  
    _getCurrencyData() {
      const raw = localStorage.getItem('currencyExchangeData');
      const data = this._safeParseJson(raw);
  
      if (!data || typeof data !== 'object') return { selectedCurrency: 'AED', rates: {} };
      if (!data.selectedCurrency) data.selectedCurrency = 'AED';
      if (!data.rates) data.rates = {};
  
      return data;
    }
  
    _getRate(currencyData) {
      const selected = currencyData.selectedCurrency || 'AED';
      if (selected === 'AED') return 1;
  
      const rate = currencyData.rates ? currencyData.rates[selected] : null;
      if (typeof rate !== 'number') return null;
      if (!Number.isFinite(rate) || rate <= 0) return null;
  
      return rate;
    }
  
    _toNumber(value) {
      if (value === null || value === undefined) return null;
      if (value === '') return null;
  
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
  
      return n;
    }
  
    _debounce(fn, waitMs) {
      let t = null;
      return function () {
        const ctx = this;
        const args = arguments;
  
        if (t) window.clearTimeout(t);
        t = window.setTimeout(function () {
          t = null;
          fn.apply(ctx, args);
        }, waitMs);
      };
    }
  
    _getCurrencySymbol(currencyCode) {
      try {
        const parts = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).formatToParts(0);
  
        for (let i = 0; i < parts.length; i++) {
          if (parts[i].type === 'currency') return parts[i].value;
        }
  
        return currencyCode;
      } catch (e) {
        return currencyCode;
      }
    }
  
    _setCurrencySymbol(currencyCode) {
      if (!this._currencySpans) return;
  
      const symbol = this._getCurrencySymbol(currencyCode);
      for (let i = 0; i < this._currencySpans.length; i++) {
        this._currencySpans[i].textContent = symbol;
      }
    }
  
    _refreshRangeSlider(rangeEl, minVal, maxVal) {
      rangeEl.setAttribute('data-se-min-value', String(minVal));
      rangeEl.setAttribute('data-se-max-value', String(maxVal));
      rangeEl.dataset.seMinValue = String(minVal);
      rangeEl.dataset.seMaxValue = String(maxVal);
  
      if (rangeEl.noUiSlider && typeof rangeEl.noUiSlider.set === 'function') {
        try {
          rangeEl.noUiSlider.set([minVal, maxVal]);
          return;
        } catch (e) {}
      }
  
      if (typeof rangeEl.refresh === 'function') {
        try {
          rangeEl.refresh();
          return;
        } catch (e) {}
      }
  
      if (typeof rangeEl.update === 'function') {
        try {
          rangeEl.update();
          return;
        } catch (e) {}
      }
  
      rangeEl.dispatchEvent(new CustomEvent('range:refresh', { bubbles: true }));
      rangeEl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  if (!customElements.get('price-range-currency-bridge')) {
    customElements.define('price-range-currency-bridge', PriceRangeCurrencyBridge);
  }  